import type {Evaluator} from "@cm/graph-v3"
import type {OnCompileContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import {graphNode} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {TemplateNode} from "#template/template-node/template-node"
import {uiStyle, textureResolution, environmentMapMode, colorValue} from "#template/nodes/validation"
import type {ColorValue, EnvironmentMapMode, TextureResolution, UiStyle} from "#template/nodes/types"
import type {TextureStreamingMode} from "@cm/material-nodes"
import {textureStreamingMode} from "@cm/material-nodes"

export type {TextureStreamingMode}
import {DataObjectReference} from "#template/nodes/image/data-object-reference"
import type {SceneNodes} from "#template/interfaces/scene-object"
import {ThisStructID} from "#template/runtime-graph/types"
import {AppendDataObjectSize} from "#template/runtime-graph/nodes/append-data-object-size"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import {visitNone} from "#template/template-node/visit-helpers"

const TD = TypeDescriptors

export type ScenePropertiesParameters = {
    maxSubdivisionLevel: number
    maxSubdivisionLevelOnMobile?: number
    backgroundColor?: ColorValue
    backgroundImage?: DataObjectReference
    shiftAndZoomBackgroundImage: boolean
    loadingBackgroundImage?: DataObjectReference
    uiColor: ColorValue
    uiStyle: UiStyle
    useSectionedMenu: boolean
    iconSize: number
    enableAr: boolean
    enableSalesEnquiry: boolean
    textureResolution?: TextureResolution
    textureFiltering: boolean
    textureStreamingMode?: TextureStreamingMode
    enableRealtimeShadows: boolean
    enableRealtimeLights: boolean
    enableRealtimeMaterials: boolean
    realtimeShadowMapOptions?: {
        resolution: number
        outerUpdateIterations: number
        innerUpdateIterations: number
        outerSmoothIterations: number
        innerSmoothIterations: number
    }
    enableOnboardingHint: boolean
    enableGltfDownload: boolean
    enableStlDownload: boolean
    enablePdfDownload: boolean
    enableSnapshot: boolean
    enableFullscreen: boolean
    environmentMapMode: EnvironmentMapMode
    showAnnotations: boolean
    enableDimensionGuides: boolean
    shadowCatcherFalloff?: {
        sizeX: number
        sizeZ: number
        smoothness: number
        opacity: number
    }
    enableAdaptiveSubdivision: boolean
    verticalArPlacement: boolean
}

export type ScenePropertiesConstructorParameters = Omit<
    ScenePropertiesParameters,
    "shiftAndZoomBackgroundImage" | "enableDimensionGuides" | "enablePdfDownload" | "verticalArPlacement" | "textureStreamingMode" | "useSectionedMenu"
> & {
    shiftAndZoomBackgroundImage?: boolean
    enableDimensionGuides?: boolean
    enablePdfDownload?: boolean
    verticalArPlacement?: boolean
    textureStreamingMode?: TextureStreamingMode
    useSectionedMenu?: boolean
}

const scenePropertiesDefinition: TemplateNodeDefinition<ScenePropertiesParameters, ScenePropertiesConstructorParameters> = {
    nodeClass: "SceneProperties",
    validation: {
        parameters: z.object({
            maxSubdivisionLevel: z.number(),
            maxSubdivisionLevelOnMobile: z.optional(z.number()),
            backgroundColor: z.optional(colorValue),
            backgroundImage: z.optional(graphNode(DataObjectReference)),
            shiftAndZoomBackgroundImage: z._default(z.boolean(), false),
            loadingBackgroundImage: z.optional(graphNode(DataObjectReference)),
            uiColor: colorValue,
            uiStyle: uiStyle,
            useSectionedMenu: z._default(z.boolean(), true),
            iconSize: z.number(),
            enableAr: z.boolean(),
            enableSalesEnquiry: z.boolean(),
            textureResolution: z.optional(textureResolution),
            textureFiltering: z.boolean(),
            textureStreamingMode: z.optional(textureStreamingMode),
            enableRealtimeShadows: z.boolean(),
            enableRealtimeLights: z.boolean(),
            enableRealtimeMaterials: z.boolean(),
            realtimeShadowMapOptions: z.optional(
                z.object({
                    resolution: z.number(),
                    outerUpdateIterations: z.number(),
                    innerUpdateIterations: z.number(),
                    outerSmoothIterations: z.number(),
                    innerSmoothIterations: z.number(),
                }),
            ),
            enableOnboardingHint: z.boolean(),
            enableGltfDownload: z.boolean(),
            enableStlDownload: z.boolean(),
            enablePdfDownload: z._default(z.boolean(), true),
            enableSnapshot: z.boolean(),
            enableFullscreen: z.boolean(),
            environmentMapMode: environmentMapMode,
            showAnnotations: z.boolean(),
            enableDimensionGuides: z._default(z.boolean(), false),
            shadowCatcherFalloff: z.optional(
                z.object({
                    sizeX: z.number(),
                    sizeZ: z.number(),
                    smoothness: z.number(),
                    opacity: z.number(),
                }),
            ),
            enableAdaptiveSubdivision: z.boolean(),
            verticalArPlacement: z._default(z.boolean(), false),
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class SceneProperties extends TemplateNode<ScenePropertiesParameters, ScenePropertiesConstructorParameters> {
    public override get definition() {
        return scenePropertiesDefinition
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<ScenePropertiesParameters> {
        const {evaluator, currentTemplate, templateDepth, path} = context
        const {preDisplayList} = currentTemplate
        const {templateScope, templateContext} = evaluator
        const {sceneManager, computeDataObjectReferences, lodType} = templateContext
        const {backgroundImage, loadingBackgroundImage, ...rest} = this.parameters

        const scope = evaluator.getScope(this)

        const newPath = scope.pureLambda(scope.tuple(path, this), ([path, instance]) => [...path, instance], "newPath", {
            output: TD.ObjectPath,
        })

        const addDataObjectReference = (dataObject: DataObjectReference | undefined) => {
            if (!dataObject) return undefined

            const result = dataObject.getDataObject(scope, evaluator)

            if (computeDataObjectReferences && lodType !== "pathTraced") {
                const referencedDataObject = scope.struct<SceneNodes.DataObjectReference>("DataObjectReference", {
                    type: "DataObjectReference",
                    id: `${scope.getPrefix()}:DataObjectReference@${dataObject.parameters.dataObjectId}`,
                    path: newPath,
                    dataObject: scope.node(AppendDataObjectSize, {sceneManager, input: result}).output,
                    origin: {
                        type: "sceneProperties",
                    },
                })

                preDisplayList.push(referencedDataObject)
            }

            return result
        }

        const sceneOptions = templateScope.struct<SceneNodes.SceneOptions>("SceneOptions", {
            type: "SceneOptions",
            templateDepth,
            id: ThisStructID,
            path: newPath,
            backgroundImage: addDataObjectReference(backgroundImage),
            loadingBackgroundImage: addDataObjectReference(loadingBackgroundImage),
            ...rest,
        })
        preDisplayList.push(sceneOptions)

        return visitNone(this.parameters)
    }
}
